import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, cx } from '@emotion/css';
import { DrawerThumbnailMultiBox } from './DrawerThumbnailMultiBox';
import { getNumPages } from './utils';
import { DrawerThumbnail } from './Thumbnails/DrawerThumbnail';
import { useDrawerData } from './DrawerProvider';
import { useUploadManager } from '../../UploadManagerProvider';
import { reaction } from 'mobx';
import { useLocalization } from '../../Localization';
const generateId = (prefix = 'focus') => {
    return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
};
/**
 * Component to display a scrollable list of assets
 */
export const DrawerThumbnailsList = (props) => {
    var _a;
    const { fetchingStatus } = useUploadManager();
    const { assets, onClick, onDoubleClick, className, hasRemoveBtn = true } = props;
    const { styleOptions } = useDrawerData();
    const [multiId, setMultiId] = useState();
    const [thumbnails, setThumbnails] = useState([]);
    const ref = useRef(null);
    const width = ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
    const { skipAssetsListHiddenButton } = useLocalization();
    const focusExitRef = useRef(null);
    const skipAssetsListLink = useMemo(() => generateId(), []);
    const updateList = useCallback(() => {
        if (fetchingStatus !== 'ready') {
            return;
        }
        const newChildren = [];
        let multiIdIndex = -1;
        let index = 0;
        assets.forEach((asset) => {
            var _a;
            const numPages = getNumPages(asset);
            const id = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.id;
            if (numPages > 1) {
                if (id === multiId) {
                    multiIdIndex = index;
                }
                newChildren.push({ asset, type: 'multi-page-thumbnail' });
            }
            else {
                newChildren.push({ asset, type: 'single-page-thumbnail' });
            }
            index += 1;
        });
        if (multiIdIndex !== -1) {
            const { asset } = newChildren[multiIdIndex];
            const thumbnailSize = styleOptions.thumbnailSize;
            const thumbnailMargin = styleOptions.flexboxGap;
            const numberOfColumns = Math.floor((width + thumbnailMargin) / (thumbnailSize + thumbnailMargin));
            const remainder = multiIdIndex % numberOfColumns;
            const index = multiIdIndex + (numberOfColumns - remainder);
            const arrowOffset = (1 / (numberOfColumns * 2) + (1 / numberOfColumns) * remainder) * 100;
            newChildren.splice(index, 0, {
                asset: asset,
                type: 'multi-page-box',
                numberOfColumns,
                arrowOffset,
            });
        }
        setThumbnails(newChildren);
    }, [assets, width, multiId, styleOptions, fetchingStatus]);
    // this reaction will monitor the data of the recently uploaded element.  if it changed then cause a re render.
    useEffect(() => {
        if (assets.length > 0) {
            const dispose = reaction(() => assets[0].data, () => {
                updateList();
            });
            return () => dispose();
        }
        return;
    }, [assets, updateList]);
    useEffect(() => {
        updateList();
    }, [updateList]);
    function toggleMultiId(id) {
        setMultiId((current) => (current === id ? undefined : id));
    }
    const resolveComponent = useCallback((child, index) => {
        var _a;
        const { type, asset, arrowOffset, numberOfColumns } = child;
        const id = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.id;
        const keyPrefix = id || index;
        switch (type) {
            case 'multi-page-thumbnail':
                return (_jsx(DrawerThumbnail, { asset: asset, onClick: () => toggleMultiId(id), hasRemoveBtn: hasRemoveBtn }, `${keyPrefix}-multi-page-thumbnail`));
            case 'multi-page-box':
                return (_jsx(DrawerThumbnailMultiBox, { asset: asset, onClick: onClick, onDoubleClick: onDoubleClick, arrowOffset: arrowOffset, numberOfColumns: numberOfColumns }, `${keyPrefix}-multi-page-box`));
            default:
                return (_jsx(DrawerThumbnail, { asset: asset, onClick: onClick, onDoubleClick: onDoubleClick, hasRemoveBtn: hasRemoveBtn }, `${keyPrefix}-basic-thumbnail`));
        }
    }, [onClick, onDoubleClick, hasRemoveBtn]);
    const style = css `
        display: flex;
        flex-flow: wrap;
        gap: ${styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.flexboxGap}px;
    `;
    const skipLink = css `
        position: absolute;
        left: -9999px;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;

        &:focus {
            position: relative;
            left: 8px;
            top: 8px;
            width: auto;
            height: auto;
            background: #fff;
            color: #000;
            padding: 8px 12px;
            z-index: 9999;
            border: 1px solid #000;
        }
    `;
    const exitList = css `
        outline: none;
        margin-top: 20px;
    `;
    return (_jsxs(_Fragment, { children: [_jsx("a", Object.assign({ href: '#' + skipAssetsListLink, className: skipLink, onClick: () => {
                    setTimeout(() => {
                        var _a;
                        (_a = focusExitRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end',
                        });
                    }, 0);
                } }, { children: skipAssetsListHiddenButton || 'skip assets list' })), _jsx("div", Object.assign({ className: cx('drawer-thumbnail-list', style, className), "data-testid": "drawer-thumbnail-list", ref: ref }, { children: thumbnails.map(resolveComponent) })), _jsx("div", { id: skipAssetsListLink, ref: focusExitRef, tabIndex: -1, className: exitList })] }));
};
DrawerThumbnailsList.displayName = 'DrawerThumbnailsList';
//# sourceMappingURL=DrawerThumbnailsList.js.map